import { useRef, useCallback, useState } from 'react';
import useEventListener from '../components/common/useEventListener';

interface UseResizeDragOptions {
  onResize: (newValue: number) => void;
  onResizeEnd?: () => void;
  minValue?: number;
  maxValue?: number;
  /**
   * Function to calculate the resize diff based on mouse movement.
   * For table columns: (e, startX) => e.clientX - startX (drag right = positive)
   * For left panel: (e, startX) => startX - e.clientX (drag left = positive)
   * For right panel: (e, startX) => e.clientX - startX (drag right = positive)
   */
  calculateDiff?: (e: MouseEvent, startX: number) => number;
}

interface UseResizeDragReturn {
  isResizing: boolean;
  isHovering: boolean;
  setIsHovering: (hovering: boolean) => void;
  handleResizeStart: (e: React.MouseEvent, initialValue: number) => void;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
}

export const useResizeDrag = ({
  onResize,
  onResizeEnd,
  minValue,
  maxValue,
  calculateDiff = (e: MouseEvent, startX: number) => e.clientX - startX, // Default: drag right = positive
}: UseResizeDragOptions): UseResizeDragReturn => {
  const [isResizing, setIsResizing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const startXRef = useRef<number>(0);
  const startValueRef = useRef<number>(0);

  const handleResizeStart = useCallback((e: React.MouseEvent, initialValue: number) => {
    e.preventDefault();
    e.stopPropagation();
    startXRef.current = e.clientX;
    startValueRef.current = initialValue;
    setIsResizing(true);
  }, []);

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      e.preventDefault();
      const diff = calculateDiff(e, startXRef.current);
      const newValue = startValueRef.current + diff;

      let constrainedValue = newValue;
      if (minValue !== undefined) {
        constrainedValue = Math.max(constrainedValue, minValue);
      }
      if (maxValue !== undefined) {
        constrainedValue = Math.min(constrainedValue, maxValue);
      }

      onResize(Number(constrainedValue.toFixed(0)));
    },
    [isResizing, onResize, minValue, maxValue, calculateDiff],
  );

  const handleResizeEnd = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setIsResizing(false);
      onResizeEnd?.();
    },
    [onResizeEnd],
  );

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!isResizing) {
      setIsHovering(false);
    }
  }, [isResizing]);

  // Use the existing useEventListener hook with conditional handlers
  const conditionalHandleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (isResizing) {
        handleResizeMove(e);
      }
    },
    [isResizing, handleResizeMove],
  );

  const conditionalHandleResizeEnd = useCallback(
    (e: MouseEvent) => {
      if (isResizing) {
        handleResizeEnd(e);
        // Note: handleResizeEnd sets isResizing to false, so this will only run once
      }
    },
    [isResizing, handleResizeEnd],
  );

  // Use the existing useEventListener hook - it handles stable refs internally
  useEventListener('mousemove', conditionalHandleResizeMove);
  useEventListener('mouseup', conditionalHandleResizeEnd);

  return {
    isResizing,
    isHovering,
    setIsHovering,
    handleResizeStart,
    handleMouseEnter,
    handleMouseLeave,
  };
};
